import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Checkbox from '@material-ui/core/Checkbox';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { IStateList, ListComponent, TableCellSortable } from 'components/Abstract/List';
import Toolbar from 'components/Layout/Toolbar';
import Alert from 'components/Shared/Alert';
import FabButton from 'components/Shared/FabButton';
import TableWrapper from 'components/Shared/TableWrapper';
import { WithStyles } from 'decorators/withStyles';
import { IDocument } from 'interfaces/document';
import { IEntity } from 'interfaces/entity';
import { IPaginationParams } from 'interfaces/pagination';
import PlusIcon from 'mdi-react/PlusIcon';
import RefreshIcon from 'mdi-react/RefreshIcon';
import TrashIcon from 'mdi-react/TrashIcon';
import React, { Fragment } from 'react';
import RxOp from 'rxjs-operators';
import documentService from 'services/document';
import DocumentFormDialog from './DocumentFormDialog';
import ListItem from './ListItem';

interface IProps {
  classes?: any;
}

interface IState extends IStateList<IDocument> {
  selected: number[];
  current?: IDocument;
  formOpened?: boolean;
  entities?: IEntity[];
}

const columns = [
  {
    title: 'Nome',
    column: 'name'
  },
  {
    title: 'Empreendimento',
    column: 'Venture.name'
  },
  {
    title: 'Periodicidade',
    column: 'frequency'
  },
  {
    title: 'Criticidade',
    column: 'criticality'
  },
  {
    title: 'Módulo',
    column: 'entityId'
  },
  {
    title: 'Aba',
    column: 'tabId'
  },
  {
    title: 'Subaba',
    column: 'subTabId'
  },
];

@WithStyles((theme) => ({
  deleteButton: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.secondary.main,
    borderColor: theme.palette.primary.main,
    borderStyle: 'solid',
    borderWidth: 2,
  },
  deleteButtonDisabled: {
    borderWidth: 0
  }
}))
export default class DocumentRegisterAndExclusionPage extends ListComponent<IProps, IState> {
  constructor(props: {}) {
    super(props, 'name');
    this.state = {
      ...this.state,
      selected: [] as number[],
      entities: [] as IEntity[],
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = (params: Partial<IPaginationParams> = {}) => {
    this.setState({ loading: true, error: null });
    documentService.list(this.mergeParams(params))
      .pipe(RxOp.logError(), RxOp.bindComponent(this))
      .subscribe(
        (items) => {
          this.setPaginatedData(items);
        },
        error => this.setError(error)
      );
  }

  handleCreate = () => {
    this.setState({ formOpened: true, current: null });
  }

  handleEdit = (current: IDocument) => {
    this.setState({ formOpened: true, current });
  }

  handleView = (current: IDocument) => {
    this.setState({ current });
  }

  formCallback = (document?: IDocument) => {
    this.setState({ formOpened: false });
    this.loadData();
  }

  formCancel = () => {
    this.setState({ formOpened: false });
  }

  handleDelete = async () => {
    const { selected, page, orderBy, pageSize, orderDirection, term } = this.state;
    const confirm = await Alert.show({ message: 'Deseja deletar os documentos selecionados?', confirmation: true });

    if (!confirm) return;

    documentService.deleteMultiple(selected).subscribe(() => {
      this.setState({ selected: [] });
      this.loadData({ page, orderBy, pageSize, orderDirection, term });
    });
  }

  onClickRefresh = () => {
    this.setState({ selected: [] });
    return this.loadData();
  };

  onSelectAllClick = () => {
    const { selected, items } = this.state;
    if (selected.length) return this.setState({ selected: [] });
    this.setState({ selected: items.map(d => d.id) });
  }

  isSelected = (id: number) => this.state.selected.indexOf(id) !== -1;

  handleClick = (id: number) => () => {
    const { selected } = this.state;
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    this.setState({ selected: newSelected });
  };

  render() {
    const { items, entities, loading, selected, formOpened, current } = this.state;
    const { classes } = this.props;
    const numSelected = selected.length;
    const rowCount = items.length;

    return (
      <Fragment>
        <Toolbar title='Gestão de documentos' breadcrumbs={[{ title: 'Configurações' }, { title: 'Gestão de documentos' }]} />
        <DocumentFormDialog
          opened={formOpened || false}
          document={current}
          onComplete={this.formCallback}
          onCancel={this.formCancel}
        />
        <Container>
          <Card>
            {this.renderLoader()}
            <CardContent>
              <Grid container justify="space-between">
                <Grid item xs={12} sm={8} lg={4}>
                  {this.renderSearch()}
                </Grid>
                <Grid item xs={12} sm='auto'>
                  <Box display='flex' justifyContent="flex-end" gridGap={8}>
                    <FabButton
                      actions={[{
                        icon: TrashIcon,
                        onClick: this.handleDelete
                      }]}
                      classes={{ primary: classes.deleteButton, disabled: classes.deleteButtonDisabled }}
                      disabled={!selected.length}
                    />
                    <FabButton
                      actions={[{
                        icon: PlusIcon,
                        onClick: this.handleCreate
                      }]}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TableWrapper minWidth={1200}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={numSelected > 0 && numSelected < rowCount}
                              checked={rowCount > 0 && numSelected === rowCount}
                              onChange={this.onSelectAllClick}
                              color='primary'
                              inputProps={{ 'aria-label': 'select all desserts' }}
                            />
                          </TableCell>
                          {columns.map((c) => (
                            <TableCellSortable {...this.sortableProps} key={`column-${c.column}`} column={c.column}>
                              {c.title}
                            </TableCellSortable>
                          ))}
                          <TableCell>
                            <IconButton disabled={loading} onClick={this.onClickRefresh}>
                              <RefreshIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {this.renderEmptyAndErrorMessages(9)}
                        {items.map(document =>
                          <ListItem
                            key={document.id}
                            entities={entities}
                            onEdit={this.handleEdit}
                            onShowHideComplete={this.loadData}
                            onDeleteComplete={this.loadData}
                            isSelected={this.isSelected(document.id)}
                            onSelect={this.handleClick(document.id)}
                            document={document}
                          />
                        )}
                      </TableBody>
                    </Table>
                  </TableWrapper>
                  {this.renderTablePagination()}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Fragment>
    );
  }
}
